import React, { useContext, useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { AuthContext } from "../../../../config/AuthContext";
import { 
    Button, Table, TableProps, Tag, Typography, Card, Space, 
    Tooltip, Progress, Badge, Empty 
} from "antd";
import { ColumnsType } from "antd/es/table";
import { 
    ReloadOutlined, EyeOutlined, CheckCircleFilled, CloseCircleFilled,
    CodeOutlined, FieldTimeOutlined, BugOutlined, ArrowRightOutlined,
    FileTextOutlined
} from '@ant-design/icons';
import { GetUsuariIdJutgeActivitatsId } from "../../../../dao/GetUsuariIdJutgeActivitatsId";
import { useParams } from 'react-router-dom';
import RevisioModal from "../../activitatsId/content/RevisioModal";

const { Text } = Typography;

interface ActivitatsIdExercicisProps {
    id_activitat?: string | number;
    id_alumne?: string | number;
    dades?: any[];
}

const ActivitatsIdExercicis = ({ id_activitat: propActivitat, id_alumne: propAlumne, dades: initialDades }: ActivitatsIdExercicisProps = {}) => {
    const { isLoggedIn, userId } = useContext(AuthContext);
    const params = useParams();
    const id_activitat = propActivitat ?? params.id_activitat ?? params.id_practica;
    const id_alumne = propAlumne ?? params.id_alumne ?? params.id_usuari ?? userId;

    const [dades, setDades] = useState<any[]>(initialDades || []);
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState(false);
    const [selected, setSelected] = useState<any>();

    useEffect(() => {
        if (initialDades && initialDades.length > 0) {
            setDades(initialDades);
        } else if (isLoggedIn && id_activitat && id_alumne) {
            checkDades();
        }
    }, [id_activitat, id_alumne, initialDades]);

    async function checkDades() {
        if (!id_activitat || !id_alumne) return;
        setLoading(true);
        try {
            const data = await GetUsuariIdJutgeActivitatsId(id_alumne + "", id_activitat + "");
            setDades(data || []);
        } catch (err) {
            console.error("Error al carregar exercicis:", err);
        } finally {
            setLoading(false);
        }
    }

    const handleOpenCode = (record: any) => {
        setSelected(record);
        setModal(true);
    };

    const closeModal = () => setModal(false);

    const decodeBase64 = (v: string | undefined | null) => {
        if (!v) return "";
        try {
            const parts = v.split(":");
            return atob(parts[parts.length - 1] || "");
        } catch {
            return v;
        }
    };

    // Columnas principales de ejercicios
    const columns: ColumnsType<any> = [
        {
            key: 'prac',
            title: 'Exercici',
            dataIndex: 'prac',
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                        background: '#f0f5ff',
                        color: '#2f54eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 12
                    }}>
                        #{record.id}
                    </div>
                    <div>
                        <div style={{ fontWeight: 600, color: '#262626', fontSize: 13 }}>{text || `Exercici #${record.id}`}</div>
                        {record.dificultat && (
                            <span style={{ fontSize: 11, color: '#8c8c8c' }}>
                                Dificultat: {record.dificultat}
                            </span>
                        )}
                    </div>
                </div>
            )
        },
        {
            key: 'estat',
            title: 'Estat',
            align: 'center',
            width: 140,
            render: (_, record) => {
                const isResolved = record.progres === 1 || (record.origen_success !== undefined && record.origen_success !== null);
                if (record.intents === 0) {
                    return (
                        <Tag color="default" style={{ borderRadius: 10, padding: '2px 10px', fontWeight: 500 }}>
                            Sense lliurament
                        </Tag>
                    );
                }
                if (isResolved) {
                    return (
                        <Tag color="success" icon={<CheckCircleFilled />} style={{ borderRadius: 10, padding: '2px 10px', fontWeight: 600 }}>
                            COMPLETAT
                        </Tag>
                    );
                }
                return (
                    <Tag color="error" icon={<CloseCircleFilled />} style={{ borderRadius: 10, padding: '2px 10px', fontWeight: 600 }}>
                        AMB ERRORS
                    </Tag>
                );
            }
        },
        {
            key: 'progres',
            title: 'Tests Superats',
            align: 'left',
            width: 190,
            render: (_, record) => {
                const totalTests = record.tests || 0;
                const passed = record.progres2 || 0;
                const percent = totalTests > 0 ? Math.round((passed / totalTests) * 100) : (record.progres === 1 ? 100 : 0);
                let color = '#f5222d';
                if (percent >= 100) color = '#52c41a';
                else if (percent >= 50) color = '#1890ff';
                else if (percent > 0) color = '#faad14';

                return (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 2 }}>
                            <span style={{ fontWeight: 600, color: '#595959' }}>{passed} / {totalTests} tests</span>
                            <span style={{ fontWeight: 700, color }}>{percent}%</span>
                        </div>
                        <Progress 
                            percent={percent} 
                            size="small" 
                            strokeColor={color} 
                            showInfo={false} 
                            style={{ margin: 0 }} 
                        />
                    </div>
                );
            }
        },
        {
            key: 'intents',
            title: 'Intents',
            dataIndex: 'intents',
            align: 'center',
            width: 100,
            render: (val) => {
                const count = val || 0;
                let color = 'default';
                if (count === 1) color = 'green';
                else if (count <= 3) color = 'blue';
                else if (count <= 6) color = 'orange';
                else if (count > 6) color = 'red';

                return (
                    <Tag color={color} style={{ fontWeight: 600, borderRadius: 8, padding: '1px 8px' }}>
                        {count} {count === 1 ? 'intent' : 'intents'}
                    </Tag>
                );
            }
        },
        {
            key: 'actions',
            title: 'Accions',
            align: 'center',
            width: 130,
            render: (_, record) => (
                <Button
                    size="small"
                    type="primary"
                    ghost
                    icon={<CodeOutlined />}
                    disabled={!record.intents || record.intents === 0}
                    onClick={() => handleOpenCode(record)}
                    style={{ borderRadius: 6, fontSize: 12, fontWeight: 500 }}
                >
                    Revisar Codi
                </Button>
            )
        }
    ];

    // Subtabla: Tests y ejecuciones del último envío del ejercicio
    const subColumns: ColumnsType<any> = [
        {
            key: 'ordre',
            title: 'Test #',
            dataIndex: 'ordre',
            width: 90,
            render: (val) => (
                <Tag style={{ borderRadius: 6, fontWeight: 600, background: '#f5f5f5', border: '1px solid #d9d9d9' }}>
                    #{val}
                </Tag>
            )
        },
        {
            key: 'estat',
            title: 'Resultat',
            dataIndex: 'estat',
            width: 120,
            align: 'center',
            render: (val) => val === 1 ? (
                <Tag color="success" icon={<CheckCircleFilled />} style={{ borderRadius: 6, fontWeight: 600 }}>
                    CORRECTE
                </Tag>
            ) : (
                <Tag color="error" icon={<CloseCircleFilled />} style={{ borderRadius: 6, fontWeight: 600 }}>
                    FALLA
                </Tag>
            )
        },
        {
            key: 'methodname',
            title: 'Mètode / Descripció',
            dataIndex: 'methodname',
            render: (val, record: any) => {
                const entrada = decodeBase64(record.entrada);
                let jsonMeta: any = null;
                try {
                    if (entrada && entrada.trim().startsWith('{')) {
                        jsonMeta = JSON.parse(entrada);
                    }
                } catch {}

                if (jsonMeta?.searchText !== undefined) {
                    return (
                        <Space size={4}>
                            <Tag color="cyan" style={{ borderRadius: 4, fontWeight: 500 }}>Contingut</Tag>
                            {jsonMeta.refClassName && <Tag color="geekblue">{jsonMeta.refClassName}</Tag>}
                            <code style={{ fontSize: 12, color: '#0958d9' }}>"{jsonMeta.searchText}"</code>
                        </Space>
                    );
                }
                if (jsonMeta?.className) {
                    return (
                        <Space size={4}>
                            <Tag color="magenta" style={{ borderRadius: 4, fontWeight: 500 }}>Clase</Tag>
                            <code style={{ fontSize: 12, color: '#722ed1', fontWeight: 600 }}>{jsonMeta.className}</code>
                        </Space>
                    );
                }
                if (val) {
                    return (
                        <code style={{ fontSize: 12, color: '#722ed1', background: '#f9f0ff', padding: '2px 6px', borderRadius: 4 }}>
                            {val}
                        </code>
                    );
                }
                return <Text type="secondary" style={{ fontSize: 12 }}>Test estàndard</Text>;
            }
        },
        {
            key: 'moment',
            title: 'Hora Enviament',
            dataIndex: 'moment',
            width: 150,
            render: (val) => (
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                    <FieldTimeOutlined style={{ marginRight: 4 }} />
                    {val ? val.split("-")[0] : '—'}
                </span>
            )
        }
    ];

    // Detalle de entrada/salida y posibles errores del test según el tipo
    const renderTestIO = (env: any) => {
        let decodedError = "";
        try {
            if (env.errors) decodedError = decodeBase64(env.errors);
        } catch {
            decodedError = "";
        }

        const rawEntrada = decodeBase64(env.entrada);
        const rawSortidaEsperada = decodeBase64(env.sortida);
        const rawSortidaAlumne = decodeBase64(env.actual);

        // Detectar tipo de test y parámetros estructurados (JSON)
        let parsedEntrada: any = null;
        try {
            if (rawEntrada && rawEntrada.trim().startsWith('{')) {
                parsedEntrada = JSON.parse(rawEntrada);
            }
        } catch {}

        // Tipo: Test de contingut / patró a classe o codi font
        const isContentTest = env.tipus === 'content' || env.tipus === 'content_class' || (parsedEntrada && parsedEntrada.searchText !== undefined);
        // Tipo: Test d'estructura de classe (class)
        const isClassTest = env.tipus === 'class' || (parsedEntrada && parsedEntrada.className && !parsedEntrada.searchText);
        // Tipo: Test de detecció d'excepcions
        const isExceptionTest = env.tipus === 'exception_check' || (parsedEntrada && parsedEntrada.exceptionType);
        // Tipo: Test d'entrada/sortida de fitxer (io_file)
        const isIoFileTest = env.tipus === 'io_file' || (parsedEntrada && (parsedEntrada.inputFileName || parsedEntrada.fileName));

        // Condición estricta pedida por el usuario:
        // Si el resultado es CORRECTE (estat === 1), NO debe salir el bloque "Error d'execució / Asserció"
        const showErrorBlock = env.estat !== 1 && decodedError && decodedError.trim() !== "";

        return (
            <div style={{
                background: '#ffffff',
                padding: '14px 18px',
                borderRadius: 8,
                border: '1px solid #e8e8e8',
                boxShadow: '0 1px 4px rgba(0,0,0,0.03)'
            }}>
                {showErrorBlock && (
                    <div style={{
                        backgroundColor: '#fff2f0',
                        borderLeft: '4px solid #ff4d4f',
                        color: '#cf1322',
                        padding: '10px 14px',
                        borderRadius: 6,
                        fontFamily: 'monospace',
                        fontSize: 12,
                        marginBottom: 14,
                        whiteSpace: 'pre-wrap'
                    }}>
                        <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <BugOutlined /> Error d'execució / Asserció:
                        </div>
                        {decodedError}
                    </div>
                )}

                {/* Si es test de contenido (content / content_class) */}
                {isContentTest && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                        <div style={{ background: '#f8f9fa', border: '1px solid #eaedf0', borderRadius: 6, padding: 12 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#595959', marginBottom: 6 }}>
                                PARÀMETRES DE LA CERCA:
                            </div>
                            <div style={{ fontSize: 12 }}>
                                {parsedEntrada?.refClassName && (
                                    <p style={{ marginBottom: 4 }}>
                                        <b>Classe a analitzar:</b> <Tag color="geekblue">{parsedEntrada.refClassName}</Tag>
                                    </p>
                                )}
                                <p style={{ marginBottom: 4 }}>
                                    <b>Contingut buscat:</b> <Tag color="purple">{parsedEntrada?.searchText || rawEntrada}</Tag>
                                </p>
                                <p style={{ marginBottom: 0 }}>
                                    <b>Condició requerida:</b>{' '}
                                    {parsedEntrada?.mustContain !== false ? (
                                        <Tag color="green">Ha de contenir</Tag>
                                    ) : (
                                        <Tag color="red">No ha de contenir</Tag>
                                    )}
                                </p>
                            </div>
                        </div>

                        <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: 12 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#389e0d', marginBottom: 6 }}>
                                CONDICIÓ ESPERADA:
                            </div>
                            <div style={{ fontSize: 12, color: '#135200' }}>
                                {parsedEntrada?.mustContain !== false
                                    ? `El codi font ha de contenir la cadena: "${parsedEntrada?.searchText || rawEntrada}"`
                                    : `El codi font NO ha de contenir la cadena: "${parsedEntrada?.searchText || rawEntrada}"`}
                            </div>
                        </div>

                        <div style={{
                            background: env.estat === 1 ? '#f6ffed' : '#fff1f0',
                            border: `1px solid ${env.estat === 1 ? '#b7eb8f' : '#ffa39e'}`,
                            borderRadius: 6,
                            padding: 12
                        }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: env.estat === 1 ? '#389e0d' : '#cf1322', marginBottom: 6 }}>
                                RESULTAT DE LA REVISIÓ:
                            </div>
                            <div style={{ fontSize: 12, color: env.estat === 1 ? '#135200' : '#a8071a' }}>
                                {env.estat === 1
                                    ? "✓ La condició s'ha complert satisfactòriament."
                                    : (rawSortidaAlumne || "✗ No s'ha complert la condició especificada al codi de l'alumne.")}
                            </div>
                        </div>
                    </div>
                )}

                {/* Si es test de estructura de clase */}
                {isClassTest && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                        <div style={{ background: '#f8f9fa', border: '1px solid #eaedf0', borderRadius: 6, padding: 12 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#595959', marginBottom: 6 }}>
                                ESTRUCTURA ESPERADA:
                            </div>
                            <div style={{ fontSize: 12 }}>
                                <p style={{ marginBottom: 4 }}>
                                    <b>Classe:</b> <Tag color="blue">{parsedEntrada?.className || 'Sense nom'}</Tag>
                                </p>
                                {parsedEntrada?.classType && (
                                    <p style={{ marginBottom: 4 }}>
                                        <b>Tipus:</b> <Tag color="magenta">{parsedEntrada.classType}</Tag>
                                    </p>
                                )}
                                {parsedEntrada?.extendedClass && (
                                    <p style={{ marginBottom: 4 }}>
                                        <b>Extén (pare):</b> <Tag color="orange">{parsedEntrada.extendedClass}</Tag>
                                    </p>
                                )}
                                {parsedEntrada?.implementedInterfaces?.length > 0 && (
                                    <p style={{ marginBottom: 4 }}>
                                        <b>Interfícies:</b> {parsedEntrada.implementedInterfaces.map((intf: string, i: number) => (
                                            <Tag key={i} color="cyan">{intf}</Tag>
                                        ))}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div style={{
                            background: env.estat === 1 ? '#f6ffed' : '#fff1f0',
                            border: `1px solid ${env.estat === 1 ? '#b7eb8f' : '#ffa39e'}`,
                            borderRadius: 6,
                            padding: 12
                        }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: env.estat === 1 ? '#389e0d' : '#cf1322', marginBottom: 6 }}>
                                RESULTAT DE LA VALIDACIÓ:
                            </div>
                            <div style={{ fontSize: 12, color: env.estat === 1 ? '#135200' : '#a8071a' }}>
                                {env.estat === 1
                                    ? "✓ La classe compleix amb tots els modificadors, mètodes i atributs esperats."
                                    : (rawSortidaAlumne || "✗ S'han trobat discrepàncies en la signatura, camps o mètodes de la classe.")}
                            </div>
                        </div>
                    </div>
                )}

                {/* Si es test de detección de excepciones */}
                {isExceptionTest && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                        <div style={{ background: '#f8f9fa', border: '1px solid #eaedf0', borderRadius: 6, padding: 12 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#595959', marginBottom: 4 }}>
                                ENTRADA (PARÀMETRES):
                            </div>
                            <pre style={{ margin: 0, fontSize: 12, maxHeight: 120, overflow: 'auto', background: 'transparent' }}>
                                {parsedEntrada?.entrada || rawEntrada || '(cap argument)'}
                            </pre>
                        </div>

                        <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, padding: 12 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#d48806', marginBottom: 4 }}>
                                EXCEPCIÓ ESPERADA:
                            </div>
                            <div style={{ fontSize: 12 }}>
                                <Tag color="red">{parsedEntrada?.exceptionType || 'Excepció'}</Tag>
                                {parsedEntrada?.expectedOutput && (
                                    <div style={{ marginTop: 6, color: '#874d00' }}>
                                        <b>Missatge:</b> {parsedEntrada.expectedOutput}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div style={{
                            background: env.estat === 1 ? '#f6ffed' : '#fff1f0',
                            border: `1px solid ${env.estat === 1 ? '#b7eb8f' : '#ffa39e'}`,
                            borderRadius: 6,
                            padding: 12
                        }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: env.estat === 1 ? '#389e0d' : '#cf1322', marginBottom: 4 }}>
                                COMPORTAMENT OBTINGUT:
                            </div>
                            <pre style={{ margin: 0, fontSize: 12, maxHeight: 120, overflow: 'auto', background: 'transparent', color: env.estat === 1 ? '#135200' : '#a8071a' }}>
                                {rawSortidaAlumne || (env.estat === 1 ? "✓ S'ha llançat l'excepció esperada correctament." : "✗ No s'ha llançat l'excepció requerida.")}
                            </pre>
                        </div>
                    </div>
                )}

                {/* Si es test estándar de Entrada / Salida o de ejecución de método */}
                {!isContentTest && !isClassTest && !isExceptionTest && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                        {rawEntrada && (
                            <div style={{ background: '#f8f9fa', border: '1px solid #eaedf0', borderRadius: 6, padding: 10 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#595959', marginBottom: 4 }}>
                                    {isIoFileTest ? 'ENTRADA PER FITXER:' : (env.methodname ? 'ARGUMENTS DEL MÈTODE:' : 'ENTRADA (ARGS):')}
                                </div>
                                <pre style={{ margin: 0, fontSize: 12, maxHeight: 120, overflow: 'auto', background: 'transparent' }}>
                                    {rawEntrada}
                                </pre>
                            </div>
                        )}

                        <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: 10 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#389e0d', marginBottom: 4 }}>
                                {env.methodname ? 'RETORN ESPERAT:' : 'SORTIDA ESPERADA:'}
                            </div>
                            <pre style={{ margin: 0, fontSize: 12, maxHeight: 120, overflow: 'auto', background: 'transparent', color: '#135200' }}>
                                {rawSortidaEsperada || '(buida)'}
                            </pre>
                        </div>

                        <div style={{
                            background: env.estat === 1 ? '#f6ffed' : '#fff1f0',
                            border: `1px solid ${env.estat === 1 ? '#b7eb8f' : '#ffa39e'}`,
                            borderRadius: 6,
                            padding: 10
                        }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: env.estat === 1 ? '#389e0d' : '#cf1322', marginBottom: 4 }}>
                                {env.methodname ? "RETORN DE L'ALUMNE:" : "SORTIDA DE L'ALUMNE:"}
                            </div>
                            <pre style={{ margin: 0, fontSize: 12, maxHeight: 120, overflow: 'auto', background: 'transparent', color: env.estat === 1 ? '#135200' : '#a8071a' }}>
                                {rawSortidaAlumne || (env.estat === 1 ? '(correcte / sense diferències)' : '(sense sortida / error)')}
                            </pre>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const tableProps: TableProps<any> = { loading };

    return (
        <Card 
            bordered={false} 
            bodyStyle={{ padding: 16 }}
            style={{ 
                borderRadius: 10, 
                background: '#ffffff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                border: '1px solid #eef0f3'
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FileTextOutlined style={{ color: '#1890ff', fontSize: 15 }} />
                    <span style={{ fontWeight: 600, fontSize: 13, color: '#1f1f1f' }}>
                        Exercicis assignats ({dades?.length || 0})
                    </span>
                </div>
                <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={checkDades}
                    loading={loading}
                    style={{ borderRadius: 6 }}
                >
                    Actualitzar
                </Button>
            </div>

            {(!dades || dades.length === 0) ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hi ha exercicis disponibles" />
            ) : (
                <Table
                    size="small"
                    showHeader
                    {...tableProps}
                    bordered={false}
                    columns={columns}
                    dataSource={dades}
                    rowKey="id"
                    pagination={false}
                    expandable={{
                        expandedRowRender: (record: any) => {
                            const enviaments = record.enviaments || [];
                            if (enviaments.length === 0) {
                                return (
                                    <div style={{ padding: '8px 16px', color: '#8c8c8c', fontStyle: 'italic', fontSize: 12 }}>
                                        L'alumne encara no ha realitzat cap enviament per aquest exercici.
                                    </div>
                                );
                            }
                            return (
                                <div style={{ padding: '12px 16px', background: '#fcfcfd', borderRadius: 8 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: '#595959', marginBottom: 8 }}>
                                        Detall de comprovació dels tests (darrer enviament):
                                    </div>
                                    <Table
                                        size="small"
                                        bordered={false}
                                        columns={subColumns}
                                        dataSource={enviaments}
                                        rowKey="moment"
                                        pagination={false}
                                        expandable={{
                                            expandedRowRender: renderTestIO,
                                            rowExpandable: () => true,
                                        }}
                                    />
                                </div>
                            );
                        },
                        rowExpandable: (record: any) => record.intents > 0,
                    }}
                />
            )}

            {modal && <RevisioModal element={selected} visible={modal} onClose={closeModal} />}
        </Card>
    );
};

export default ActivitatsIdExercicis;
